import React from "react";
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
  Font
} from "@react-pdf/renderer";

Font.register({
  family: "Exo2",
  src: "/fonts/Exo2-VariableFont_wght.ttf"
});

/* ===================== STYLES ===================== */
const styles = StyleSheet.create({
  page: {
    padding: 24,
    fontSize: 6,
    fontFamily: "Exo2"
  },

  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12
  },

  logo: {
    width: 120
  },

  section: {
    marginBottom: 12
  },

  halfWidth: {
    width: "50%",
    marginBottom: 10,
  },

  row: {
    flexDirection: "row"
  },

  cell: {
    padding: 2,
  },

  bold: {
    fontWeight: "bold"
  },

  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#323B98",
    color: "white"
  },

  tableRow: {
    flexDirection: "row",
    borderBottom: "1px solid #eee"
  },

  balanceCell: {
    fontSize: 6
  },

  colEspecie: { width: "16%" },
  colTicker: { width: "5%" },
  colIsin: { width: "9%" },
  colEstado: { width: "7%" },
  colCantidad: { width: "12%" },
  colPrecio: { width: "9%" },
  colImporte: { width: "11%" },
  colCosto: { width: "8%" },
  colPart: { width: "5%" },
  colPct: { width: "4%" },
  colResultado: { width: "13%" },
  colLabel: {
    width: "45%",
    fontSize: 6,
  },
  colExtra: {
    width: "20%",
    fontSize: 6,
  },
  colAmount: {
    width: "35%",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  }, currency: {
    fontSize: 6,
  },

  amount: {
    fontSize: 6,
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },
  right: {
    textAlign: "right"
  },

  rightAlign: {
    textAlign: "right",
    fontSize: 6,
    color: "#444"
  },

  infoRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8
  },

  infoItem: {
    flexDirection: "row",
    alignItems: "center",
    marginRight: 12, // reemplaza gap
    fontSize: 6,
    color: "#000"
  },
  subtotalRow: {
    borderTop: "1px solid #999",
    marginTop: 2
  },
  center: {
    textAlign: "center"
  },
  amountCell: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },

  amountCurrency: {
    fontSize: 6,
  },

  amountValue: {
    fontSize: 6,
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },

  negativo: {
    color: "red",
  },
  sectionHalf: {
    width: "50%",
    marginBottom: 10,
  },

});

/* ===================== COMPONENT ===================== */
export default function VistaPdfExport({
  homeTrade,
  portafolioTotal,
  portafolioDisponible,
  portafolioGarantia,
  portafolioTotalUSD,
  portafolioDisponibleUSD,
  portafolioGarantiaUSD,
  entityBalance,
  entityBalanceUSD,
  entityHolding,
  entityHoldingUSD,
  totalSubTotal,
  totalSubTotalUSD,
  tipoCambio,
  tipoCambioCcl,
  amountCtaCte,
  amountCtaCteUSD,
  filterManager,
  viewManager,
  viewComitente,
  filterComitente,
  filterDate
}) {

  const fecha =
    filterDate?.split("-") ?? null;

  const balances =
    homeTrade === "ars" ? entityBalance : entityBalanceUSD;


  const formatNum = (value) => {
    if (value === null || value === undefined) return "";
    return Number(value).toLocaleString("es-AR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const renderHoldingsPdf = (entityHolding, currency) => {
    let subtotal = null;
    let subtotalAmount = 0;
    let subtotalCost = 0;
    let subtotalResult = 0;

    return entityHolding.flatMap((item, index) => {
      return item.status.map((st, g) => {
        const rows = [];

        // 🔹 Cambio de grupo → subtotal
        if (subtotal !== null && item.subtotal !== subtotal) {
          rows.push(renderHoldingSubtotalPdf(
            subtotal,
            subtotalAmount,
            subtotalCost,
            subtotalResult,
            currency
          ));
          subtotalAmount = 0;
          subtotalCost = 0;
          subtotalResult = 0;
        }

        subtotal = item.subtotal;

        subtotalAmount += Number(currency === 'ARS' ? st.amountFx : st.amountUsd);
        subtotalCost += Number(currency === 'ARS' ? st.costFx : st.costUsd);
        subtotalResult += Number(currency === 'ARS' ? st.profitFx : st.profitUsd);

        // 🔹 Fila normal
        rows.push(
          <View key={`${index}-${g}`} style={styles.tableRow}>
            <Text style={[styles.cell, styles.colEspecie, styles.bold, styles.balanceCell]}>
              {g === 0 ? item.item : ""}
            </Text>

            <Text style={[styles.cell, styles.colTicker, styles.balanceCell]}>
              {g === 0 ? item.symbol : ""}
            </Text>

            <Text style={[styles.cell, styles.colIsin, styles.balanceCell]}>
              {g === 0 ? item.isin : ""}
            </Text>

            <Text style={[styles.cell, styles.colEstado, styles.balanceCell]}>
              {st.status}
            </Text>

            <Text style={[styles.cell, styles.colCantidad, styles.right, styles.balanceCell]}>
              {formatNum(st.qty)}
            </Text>

            <Text style={[styles.cell, styles.colPrecio, styles.right, styles.balanceCell]}>
              {formatNum(currency === 'ARS' ? st.fxPrice : st.fxPriceUsd)}
            </Text>

            <Text style={[styles.cell, styles.colImporte, styles.right, styles.balanceCell]}>
              {formatNum(currency === 'ARS' ? st.amountFx : st.amountUsd)}
            </Text>

            <Text style={[styles.cell, styles.colPart, styles.right, styles.balanceCell]}>
              {formatNum(st.weightPerc)}%
            </Text>

            <Text style={[styles.cell, styles.colCosto, styles.right, styles.balanceCell]}>
              {formatNum(currency === 'ARS' ? st.costFx : st.costUsd)}
            </Text>

            <Text style={[styles.cell, styles.colPct, styles.right, styles.balanceCell]}>
              {formatNum(currency === 'ARS' ? st.profitPerc : st.profitUsdPerc)}
            </Text>
            <Text
              style={[
                styles.cell,
                styles.colResultado,
                styles.right,
                Number(currency === 'ARS' ? st.profitPerc : st.profitUsdPerc) < 0 && { color: "red" }, styles.balanceCell
              ]}
            >
              {formatNum(currency === 'ARS' ? st.profitFx : st.profitUsd)}
            </Text>
          </View>
        );

        // 🔹 Último item → subtotal final
        if (index === entityHolding.length - 1 && g === item.status.length - 1) {
          rows.push(renderHoldingSubtotalPdf(
            subtotal,
            subtotalAmount,
            subtotalCost,
            subtotalResult,
            currency
          ));
        }

        return rows;
      });
    });
  };

  const renderHoldingSubtotalPdf = (label, amount, cost, result, currency) => (
    <View style={[styles.tableRow, styles.subtotalRow]} key={`sub-${label}`}>
      <Text style={[styles.cell, styles.colEspecie, styles.bold, styles.balanceCell]}>
        Subtotal {label}
      </Text>

      <Text style={[styles.cell, styles.colTicker, styles.balanceCell]} />
      <Text style={[styles.cell, styles.colIsin, styles.balanceCell]} />
      <Text style={[styles.cell, styles.colEstado, styles.balanceCell]} />
      <Text style={[styles.cell, styles.colCantidad, styles.balanceCell]} />
      <Text style={[styles.cell, styles.colPrecio, styles.balanceCell]} />

      <Text style={[styles.cell, styles.colImporte, styles.right, styles.balanceCell]}>
        {formatNum(amount)}
      </Text>
      <Text style={[styles.cell, styles.colPart, styles.balanceCell]} />
      <Text style={[styles.cell, styles.colCosto, styles.right, styles.balanceCell]}>
        {formatNum(cost)}
      </Text>

      <Text style={[styles.cell, styles.colPct, styles.balanceCell]} />

      <Text
        style={[
          styles.cell,
          styles.colResultado,
          styles.right,
          result < 0 && { color: "red" }, styles.balanceCell
        ]}
      >
        {formatNum(result)}
      </Text>
    </View>
  );

  const buildBalanceRows = (entityBalance, homeTrade) => {
    const rows = [];

    entityBalance.forEach((item) => {
      item.data.forEach((d, idx) => {
        let cantidad = 0;
        let importe = 0;
        let monedaCant = "";
        let monedaImporte = "";
        let fieldValue = 0;

        if (homeTrade === "ars") {
          if (item.name === "PESOS") {
            cantidad = d.amount;
            importe = d.amountFx;
            monedaCant = "ARS";
            monedaImporte = "ARS";
            fieldValue = d.amountFx;
          }
          if (item.name === "USD MEP") {
            cantidad = d.amountMep;
            importe = d.amountFxMep;
            monedaCant = "USD";
            monedaImporte = "ARS";
            fieldValue = d.amountFxMep;
          }
          if (item.name === "USD CABLE") {
            cantidad = d.amountCcl;
            importe = d.amountFxCcl;
            monedaCant = "USD";
            monedaImporte = "ARS";
            fieldValue = d.amountFxCcl;
          }
        }

        if (homeTrade === "usd") {
          if (item.name === "PESOS") {
            cantidad = d.amount;
            importe = d.amountMepFx;
            monedaCant = "ARS";
            monedaImporte = "USD";
            fieldValue = d.amountMepFx;
          }
          if (item.name === "USD MEP") {
            cantidad = d.amountMep;
            importe = d.amountMep;
            monedaCant = "USD";
            monedaImporte = "USD";
            fieldValue = d.amountMep;
          }
          if (item.name === "USD CABLE") {
            cantidad = d.amountCcl;
            importe = d.amountCcl;
            monedaCant = "USD";
            monedaImporte = "USD";
            fieldValue = d.amountCcl;
          }
        }

        rows.push({
          grupo: idx === 0 ? item.name : "",
          estado: d.status,
          monedaCant,
          cantidad: formatNum(cantidad),
          monedaImporte,
          importe: formatNum(importe),
          negativo: Number(fieldValue) < 0
        });
      });
    });

    return rows;
  };

  const balanceRows = buildBalanceRows(
    homeTrade === "ars" ? entityBalance : entityBalanceUSD,
    homeTrade
  );

  const PdfRow3Cols = ({
    label,
    extra,
    moneda,
    value,
    negativo,
    bold,
  }) => (
    <View style={styles.tableRow}>
      {/* Col 1: Concepto */}
      <Text style={[styles.cell, styles.colLabel, bold && styles.bold]}>
        {label}
      </Text>

      {/* Col 2: TC u vacío */}
      <Text style={[styles.cell, styles.colExtra]}>
        {extra || ""}
      </Text>

      {/* Col 3: Importe */}
      <View style={styles.colAmount}>
        <Text style={[styles.cell, styles.currency]}>
          {moneda}
        </Text>
        <Text
          style={[
            styles.cell,
            styles.amount,
            negativo && styles.negativo
          ]}
        >
          {value}
        </Text>
      </View>
    </View>
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>

        {/* ===================== HEADER ===================== */}
        <View style={styles.header}>
          <Image
            src="/img/winvest.png"
            style={styles.logo}
          />

          <Text style={styles.rightAlign}>
            {new Date().toLocaleString("es-AR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit"
            })}
          </Text>
        </View>

        {/* ===================== INFO ===================== */}
        <View style={styles.infoRow}>
          {/* Moneda */}
          <View style={styles.infoItem}>
            <Text style={styles.bold}>Moneda:</Text>
            <Text> {homeTrade === "ars" ? "ARS" : "USD"}</Text>
          </View>

          {/* Fecha */}
          {fecha && (
            <View style={styles.infoItem}>
              <Text style={styles.bold}>Fecha:</Text>
              <Text> {`${fecha[2]}/${fecha[1]}/${fecha[0]}`}</Text>
            </View>
          )}

          {/* Cuenta */}
          {viewComitente && filterComitente && (
            <View style={styles.infoItem}>
              <Text style={styles.bold}>Cuenta:</Text>
              <Text> {filterComitente.label}</Text>
            </View>
          )}

          {/* Agente */}
          {viewManager && filterManager && (
            <View style={styles.infoItem}>
              <Text style={styles.bold}>Agente:</Text>
              <Text> {filterManager.label}</Text>
            </View>
          )}
        </View>

        {/* ===================== RESUMEN ===================== */}
        <View style={[styles.section, styles.halfWidth]}>

          <PdfRow3Cols
            label="Portafolio"
            extra=""
            moneda={homeTrade === "ars" ? "$" : "USD"}
            value={
              homeTrade === "ars"
                ? portafolioTotal.text
                : portafolioTotalUSD.text
            }
            negativo={
              Number(
                homeTrade === "ars"
                  ? portafolioTotal.number
                  : portafolioTotalUSD.number
              ) < 0
            }
            bold
          />

          <PdfRow3Cols
            label="Disponible"
            extra=""
            moneda={homeTrade === "ars" ? "$" : "USD"}
            value={
              homeTrade === "ars"
                ? portafolioDisponible.text
                : portafolioDisponibleUSD.text
            }
            negativo={
              Number(
                homeTrade === "ars"
                  ? portafolioDisponible.number
                  : portafolioDisponibleUSD.number
              ) < 0
            }
            bold
          />

          <PdfRow3Cols
            label="Garantía"
            extra=""
            moneda={homeTrade === "ars" ? "$" : "USD"}
            value={
              homeTrade === "ars"
                ? portafolioGarantia.text
                : portafolioGarantiaUSD.text
            }
            negativo={
              Number(
                homeTrade === "ars"
                  ? portafolioGarantia.number
                  : portafolioGarantiaUSD.number
              ) < 0
            }
            bold
          />

          {/* DETALLE POR CUENTA */}
          {balances.map((item, i) => {
            const amount =
              homeTrade === "ars"
                ? amountCtaCte(item)
                : amountCtaCteUSD(item);

            return (
              <PdfRow3Cols
                key={i}
                label={item.name}
                extra={
                  item.name === "USD MEP"
                    ? `TC: ${tipoCambio}`
                    : item.name === "USD CABLE"
                      ? `TC: ${tipoCambioCcl}`
                      : ""
                }
                moneda={homeTrade === "ars" ? "$" : "USD"}
                value={amount.text}
                negativo={Number(amount.number) < 0}
              />
            );
          })}
        </View>



        <View style={styles.section}>

          <View style={styles.tableHeader}>
            <Text style={[styles.cell, styles.colEspecie, styles.bold, styles.balanceCell, { color: "white" }]}>
              Especie
            </Text>
            <Text style={[styles.cell, styles.colTicker, styles.bold, styles.center, styles.balanceCell, { color: "white" }]}>
              Ticker
            </Text>
            <Text style={[styles.cell, styles.colIsin, styles.bold, styles.center, styles.balanceCell, { color: "white" }]}>
              ISIN
            </Text>
            <Text style={[styles.cell, styles.colEstado, styles.bold, styles.center, styles.balanceCell, { color: "white" }]}>
              Estado
            </Text>
            <Text style={[styles.cell, styles.colCantidad, styles.bold, styles.right, styles.balanceCell, { color: "white" }]}>
              Cantidad
            </Text>
            <Text style={[styles.cell, styles.colPrecio, styles.bold, styles.right, styles.balanceCell, { color: "white" }]}>
              Precio
            </Text>
            <Text style={[styles.cell, styles.colImporte, styles.bold, styles.right, styles.balanceCell, { color: "white" }]}>
              Importe
            </Text>
            <Text style={[styles.cell, styles.colPart, styles.bold, styles.right, styles.balanceCell, { color: "white" }]}>
              Part. %
            </Text>
            <Text style={[styles.cell, styles.colCosto, styles.bold, styles.right, styles.balanceCell, { color: "white" }]}>
              Costo
            </Text>
            <Text style={[styles.cell, styles.colPct, styles.bold, styles.right, styles.balanceCell, { color: "white" }]}>
              Var %
            </Text>
            <Text style={[styles.cell, styles.colResultado, styles.bold, styles.right, styles.balanceCell, { color: "white" }]}>
              Resultado
            </Text>
          </View>

          {balanceRows.map((row, i) => (
            <View key={i} style={styles.tableRow}>

              {/* ESPECIE */}
              <Text style={[styles.cell, styles.colEspecie, styles.bold, styles.balanceCell]}>
                {row.grupo}
              </Text>

              <Text style={[styles.cell, styles.colTicker]} />
              <Text style={[styles.cell, styles.colIsin]} />

              {/* ESTADO */}
              <Text style={[styles.cell, styles.colEstado, styles.balanceCell]}>
                {row.estado}
              </Text>

              {/* CANTIDAD */}
              <View style={[styles.cell, styles.colCantidad, styles.balanceCell, row.negativo && { color: "red" }]}>
                <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
                  <Text>{row.monedaCant}</Text>
                  <Text style={styles.right}>{row.cantidad}</Text>
                </View>
              </View>

              <Text style={[styles.cell, styles.colPrecio]} />
              <View style={[styles.cell, styles.colImporte, styles.balanceCell, row.negativo && { color: "red" }]}>
                <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
                  <Text>{row.monedaImporte}</Text>
                  <Text style={styles.right}>{row.importe}</Text>
                </View>
              </View>
              <Text style={[styles.cell, styles.colPart]} />
              <Text style={[styles.cell, styles.colCosto]} />
              <Text style={[styles.cell, styles.colPct]} />

              {/* RESULTADO */}
              <View
                style={[
                  styles.cell,
                  styles.colResultado,
                  styles.balanceCell,
                  row.negativo && { color: "red" }
                ]}
              >
                <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
                  <Text>{row.monedaImporte}</Text>
                  <Text style={styles.right}>{row.importe}</Text>
                </View>
              </View>

            </View>
          ))}

          <View style={[styles.tableRow, styles.subtotalRow]}>

            {/* ESPECIE */}
            <Text style={[styles.cell, styles.colEspecie, styles.bold, styles.balanceCell]}>
              Subtotal Cuenta Corriente
            </Text>

            {/* TICKER */}
            <Text style={[styles.cell, styles.colTicker]} />

            {/* ISIN */}
            <Text style={[styles.cell, styles.colIsin]} />

            {/* ESTADO */}
            <Text style={[styles.cell, styles.colEstado]} />

            {/* CANTIDAD */}
            <Text style={[styles.cell, styles.colCantidad]} />

            {/* PRECIO */}
            <Text style={[styles.cell, styles.colPrecio]} />

            {/* IMPORTE */}
            <View style={[styles.cell, styles.colImporte, styles.balanceCell]}>
              <Text
                style={[
                  styles.right,
                  Number(
                    homeTrade === "ars"
                      ? totalSubTotal.number
                      : totalSubTotalUSD.number
                  ) < 0 && { color: "red" }
                ]}
              >
                {homeTrade === "ars"
                  ? totalSubTotal.text
                  : totalSubTotalUSD.text}
              </Text>
            </View>

            <Text style={[styles.cell, styles.colPart]} />
            {/* COSTO */}
            <Text style={[styles.cell, styles.colCosto]} />

            {/* % */}
            <Text style={[styles.cell, styles.colPct]} />

            {/* RESULTADO */}
            <View style={[styles.cell, styles.colResultado, styles.balanceCell]}>
              <Text
                style={[
                  styles.right,
                  Number(
                    homeTrade === "ars"
                      ? totalSubTotal.number
                      : totalSubTotalUSD.number
                  ) < 0 && { color: "red" }
                ]}
              >
                {homeTrade === "ars"
                  ? totalSubTotal.text
                  : totalSubTotalUSD.text}
              </Text>
            </View>

          </View>

          {homeTrade === "ars" &&
            renderHoldingsPdf(entityHolding, "ARS")}

          {homeTrade === "usd" &&
            renderHoldingsPdf(entityHoldingUSD, "USD")}

        </View>

      </Page>
    </Document>
  );
}
